import * as d3 from 'd3'
import { Component, RefObject, createRef } from 'react'

export interface Bar {
  name: string
  value: number
}

interface BarChartProps {
  title?: string
  data: Bar[]
  width?: number
  height?: number
  valueFormatter?: (value: number) => string
  xAxisRotate?: boolean
  marginRight?: number
  className?: string
}

export default class BarChart extends Component<BarChartProps> {
  svgRef: RefObject<SVGElement> = createRef()

  // @ts-ignore
  _prevData: Bar[]

  componentDidMount() {
    this.renderChart()
  }

  componentDidUpdate() {
    this.renderChart()
  }

  renderChart() {
    const { data, title } = this.props
    if (data === this._prevData) {
      return
    }
    this._prevData = data
    this.svgRef.current!.innerHTML = ''

    const xAxisRotate = this.props.xAxisRotate === true

    var margin = { top: title ? 20 : 10, right: this.props.marginRight || 60, bottom: xAxisRotate ? 70 : 20, left: 15 }
    const totalWidth = this.props.width || 440
    const totalHeight = this.props.height || 330
    var width = totalWidth - margin.left - margin.right
    var height = totalHeight - margin.top - margin.bottom

    const x = d3
      .scaleBand()
      .domain(data.map((d) => d.name))
      .rangeRound([0, width])
      .padding(0.1)

    let min = d3.min(data, (d: Bar) => d.value)!
    if (min < 0) {
      min = d3.min(data, (d: Bar) => Math.min(d.value, -d.value))!
    } else {
      min = 0
    }

    const y = d3
      .scaleLinear()
      .range([height, 0])
      .domain([min, d3.max(data, (d: Bar) => Math.abs(d.value))!])
      .nice()
    const yFormat = this.props.valueFormatter ? this.props.valueFormatter : y.tickFormat()

    const svg = d3
      .select(this.svgRef.current)
      .property('value', [])
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')')

    var xAxis = d3.axisBottom(x)
    var yAxis = d3.axisRight(y).ticks(height > 200 ? 10 : 5)

    const makeYLines = () => d3.axisLeft(y)
    // @ts-ignore - tickFormat arg can be any string
    svg.append('g').attr('class', 'grid').call(makeYLines().tickSize(-width).tickSizeOuter(0).tickFormat(''))

    svg
      .append('g')
      .attr('class', 'x-axis axis')
      .attr('transform', 'translate(0,' + height + ')')
      .call(xAxis)

    svg
      .selectAll('.x-axis text')
      .style('text-anchor', xAxisRotate ? 'end' : 'middle')
      .attr('dx', xAxisRotate ? '-.8em' : 0)
      .attr('dy', xAxisRotate ? '.15em' : '0.8em')
      .attr('transform', 'rotate(' + (xAxisRotate ? -65 : 0) + ')')

    svg
      .append('g')
      .attr('class', 'y-axis axis')
      .attr('transform', 'translate(' + width + ', 0)')
      .call(yAxis)

    if (title) {
      svg
        .append('text')
        .attr('class', 'bar-chart-title')
        .attr('x', 4)
        .attr('y', -5)
        .attr('text-anchor', 'start')
        .text(title)
    }

    const getHeight = (d: Bar) => {
      let height = Math.abs(y(d.value ?? 0) - y(0))
      if (d.value > 0 || d.value < 0) {
        height = Math.max(height, 0.5)
      }
      return height
    }
    const isOutside = (height: number) => height < 14
    let bars = svg.selectAll('.bar').data(data)
    bars
      .enter()
      .append('rect')
      .attr('class', function (d: Bar) {
        return 'bar bar--' + (d.value < 0 ? 'negative' : 'positive')
      })
      .attr('x', (d: Bar) => x(d.name)!)
      .attr('y', (d: Bar) => y(Math.max(0, d.value)))
      .attr('height', getHeight)
      .attr('width', x.bandwidth())
      .attr('ry', FinvizSettings?.hasRedesignEnabled ? 2 : 0)

    svg
      .selectAll('.bar-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'divergence')
      .attr('x', (d: Bar) => x(d.name)! + x.bandwidth() / 2)
      .attr('y', (d: Bar) => {
        const height = getHeight(d)
        const outside = isOutside(height)
        if (d.value < 0) {
          return y(Math.max(0, d.value)) + height + (outside ? 12 : -4)
        }
        return y(Math.max(0, d.value)) + (outside ? -4 : 12)
      })
      .attr('fill', (d: Bar) => (!FinvizSettings.hasDarkTheme && isOutside(getHeight(d)) ? 'black' : 'white'))
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .text((d: Bar) => (d.value !== null ? yFormat(d.value) : ''))

    this.svgRef.current!.style.width = totalWidth + 'px'
    this.svgRef.current!.style.height = totalHeight + 'px'
  }

  render() {
    return (
      <svg
        // @ts-ignore
        ref={this.svgRef}
        className={'quote-bar-chart ' + (this.props.className || '')}
      ></svg>
    )
  }
}
